import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { Upload, FileText, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import { groupByReferenceMonth } from '../lib/referencePeriod';

/**
 * Reads the download name sent by the API.
 */
function fileNameFromDisposition(header, fallback) {
  const match = /filename="([^"]+)"/.exec(header || '');
  return match ? match[1] : fallback;
}

/**
 * Reads an error message from a blob response.
 */
async function downloadErrorMessage(error, fallback) {
  const payload = error.response?.data;
  if (payload instanceof Blob) {
    try {
      return JSON.parse(await payload.text()).error || fallback;
    } catch (parseError) {
      return fallback;
    }
  }
  return error.response?.data?.error || fallback;
}

/**
 * Formats a notebook total as Brazilian currency.
 */
function formatMoney(value) {
  return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/**
 * Formats the processing timestamp for the file list.
 */
function formatDate(value) {
  if (!value) return '';
  return new Date(value).toLocaleString('pt-BR');
}

function GuaibimAssistUpload() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [files, setFiles] = useState([]);
  const [downloadingKey, setDownloadingKey] = useState(null);

  useEffect(() => {
    loadFiles();
  }, []);

  const loadFiles = async () => {
    try {
      const response = await api.get('/guaibim-assist/files');
      setFiles(response.data.files || []);
    } catch (error) {
      console.error('Erro ao carregar arquivos GUAIBIM-ASSIST:', error);
    }
  };

  const handleUpload = async (event) => {
    event.preventDefault();
    if (!selectedFile) {
      toast.error('Selecione um arquivo CSV');
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      setUploading(true);
      const response = await api.post('/guaibim-assist/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResult(response.data.file);
      if (response.data.duplicate) {
        toast.success('Este arquivo já tinha sido processado. Os totais foram carregados.');
      } else {
        toast.success('Arquivo processado com sucesso');
      }
      setSelectedFile(null);
      event.target.reset();
      await loadFiles();
    } catch (error) {
      console.error('Erro ao enviar CSV GUAIBIM-ASSIST:', error);
      toast.error(error.response?.data?.error || 'Erro ao processar o arquivo');
    } finally {
      setUploading(false);
    }
  };

  /**
   * Downloads the conference workbook or the invoice PDF of one processed CSV.
   */
  const downloadGeneratedFile = async (file, kind) => {
    const key = `${file.id}:${kind}`;
    const isPdf = kind === 'pdf';
    try {
      setDownloadingKey(key);
      const response = await api.get(`/guaibim-assist/files/${file.id}/${isPdf ? 'invoice' : 'excel'}`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], {
        type: isPdf
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const baseName = String(file.originalName || 'ASSIST_GUAIBIM').replace(/\.csv$/i, '');
      link.href = url;
      link.download = fileNameFromDisposition(
        response.headers['content-disposition'],
        isPdf ? 'FATURA_GUAIBIM_RESOLVE.pdf' : `${baseName}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao baixar arquivo GUAIBIM-ASSIST:', error);
      toast.error(await downloadErrorMessage(error, isPdf ? 'Erro ao gerar a fatura' : 'Erro ao gerar o Excel'));
    } finally {
      setDownloadingKey(null);
    }
  };

  return (
    <Layout
      headerTitle="GUAIBIM RESOLVE"
      headerSubtitle="Upload e processamento do arquivo de assistência"
    >
      <div className="p-6">
        <div className="max-w-5xl mx-auto space-y-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">GUAIBIM RESOLVE</h1>
            <p className="text-gray-600">
              Envie o CSV separado por ponto e vírgula para calcular o valor do seguro, os cancelamentos e o valor a faturar. Depois baixe o Excel de conferência ou a fatura em PDF de cada arquivo processado.
            </p>
          </div>

          <form onSubmit={handleUpload} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Arquivo CSV</label>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(event) => setSelectedFile(event.target.files?.[0] || null)}
                className="flex-1 block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-primary-50 file:text-primary-700"
              />
              <button
                type="submit"
                disabled={uploading}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-60"
              >
                <Upload className="h-4 w-4" />
                {uploading ? 'Processando...' : 'Processar'}
              </button>
            </div>
            {selectedFile && (
              <p className="text-sm text-gray-500 mt-2">{selectedFile.name}</p>
            )}
          </form>

          {result && (
            <Summary
              result={result}
              downloadingExcel={downloadingKey === `${result.id}:excel`}
              downloadingPdf={downloadingKey === `${result.id}:pdf`}
              onDownloadExcel={() => downloadGeneratedFile(result, 'excel')}
              onDownloadPdf={() => downloadGeneratedFile(result, 'pdf')}
            />
          )}

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Arquivos processados</h2>
            </div>
            {files.length === 0 ? (
              <p className="px-6 py-8 text-sm text-gray-500">Nenhum arquivo processado ainda.</p>
            ) : (
              groupByReferenceMonth(files, (file) => file.originalName).map((group) => (
              <div key={group.key}>
                <div className="px-6 py-2 bg-gray-100 text-sm font-semibold text-gray-800">{group.label}</div>
              <ul className="divide-y divide-gray-200">
                {group.items.map((file) => (
                  <li key={file.id} className="px-6 py-4 hover:bg-gray-50 flex items-center gap-4">
                    <button
                      type="button"
                      onClick={() => setResult(file)}
                      className="flex-1 text-left flex items-center justify-between gap-4 min-w-0"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <FileText className="h-5 w-5 text-primary-600 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{file.originalName}</p>
                          <p className="text-xs text-gray-500">{formatDate(file.createdAt)} · {file.rowCount} registros</p>
                        </div>
                      </div>
                      <p className="text-sm font-medium text-gray-900">{formatMoney(file.totalFaturar)}</p>
                    </button>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => downloadGeneratedFile(file, 'excel')}
                        disabled={downloadingKey === `${file.id}:excel`}
                        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 disabled:opacity-60"
                      >
                        <Download className="h-4 w-4" />
                        {downloadingKey === `${file.id}:excel` ? 'Gerando...' : 'Excel'}
                      </button>
                      <button
                        type="button"
                        onClick={() => downloadGeneratedFile(file, 'pdf')}
                        disabled={downloadingKey === `${file.id}:pdf`}
                        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-60"
                      >
                        <Download className="h-4 w-4" />
                        {downloadingKey === `${file.id}:pdf` ? 'Gerando...' : 'Fatura'}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
              </div>
              ))
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

/**
 * Shows the three notebook totals and the active versus cancelled breakdown.
 */
function Summary({ result, downloadingExcel, downloadingPdf, onDownloadExcel, onDownloadPdf }) {
  const cards = [
    { label: 'Valor total do seguro', value: formatMoney(result.totalValorSeguro) },
    { label: 'Cancelados', value: formatMoney(result.totalCancelado) },
    { label: 'Valor a faturar', value: formatMoney(result.totalFaturar) }
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2 flex-wrap">
        <button
          type="button"
          onClick={onDownloadExcel}
          disabled={downloadingExcel}
          className="inline-flex items-center gap-2 px-4 py-2 text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 disabled:opacity-60"
        >
          <Download className="h-4 w-4" />
          {downloadingExcel ? 'Gerando Excel...' : 'Baixar Excel de conferência'}
        </button>
        <button
          type="button"
          onClick={onDownloadPdf}
          disabled={downloadingPdf}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-60"
        >
          <Download className="h-4 w-4" />
          {downloadingPdf ? 'Gerando fatura...' : 'Baixar fatura PDF'}
        </button>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {cards.map((card) => (
          <div key={card.label} className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
            <p className="text-sm text-gray-500">{card.label}</p>
            <p className="text-2xl font-semibold text-gray-900 mt-1">{card.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total de registros</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {(result.summary || []).map((item) => (
              <tr key={item.status}>
                <td className="px-6 py-4 text-sm text-gray-900">{item.status}</td>
                <td className="px-6 py-4 text-sm text-gray-900">{item.totalRegistros}</td>
                <td className="px-6 py-4 text-sm text-gray-900">{formatMoney(item.valorTotal)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default GuaibimAssistUpload;
